import React from "react";
import { Link } from "react-router-dom";
import { Building2, Users, Landmark } from "lucide-react";

const HeroSection = () => {
  return (
    <section className="bg-black text-white relative overflow-hidden">
      {/* Background image */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1597733336794-12d05021d510')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-blue-900/40 to-black/70"></div>
      
      {/* Background decorative elements */}
      <div className="absolute right-0 top-0 w-96 h-96 bg-orange-500 rounded-full opacity-10 blur-3xl transform translate-x-32 -translate-y-32"></div>
      <div className="absolute right-32 bottom-0 w-64 h-64 bg-orange-600 rounded-full opacity-10 blur-3xl transform translate-y-32"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-20" style={{ paddingTop: '220px' }}>
        {/* Main hero text */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            La velocità del tuo <span className="text-orange-500">BUSINESS</span> merita la connessione giusta per andare sempre oltre
          </h1>
          <p className="text-2xl italic text-gray-400 mb-8">
            Più veloce. Più connesso. Più futuro.
          </p>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-12">
            <strong>LOVECCHIO GROUP</strong> è il partner strategico che offre connettività e soluzioni digitali a 
            <strong className="text-orange-400"> Piccole e Medie Imprese, Grandi Aziende e Pubblica Amministrazione</strong>
          </p>
        </div>
        
        {/* Service categories */}
        <div className="text-center mb-16">
          <h3 className="text-3xl font-bold mb-12">
            WindTre <span className="text-orange-500">BUSINESS</span>. I tuoi progetti, connessi.
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Small/Medium Business */}
            <Link 
              to="/piccole-medie-imprese"
              className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-2xl p-8 hover:shadow-2xl hover:shadow-orange-500/30 transition-all duration-300 transform hover:scale-105 group border border-gray-700 hover:border-orange-500 overflow-hidden"
            >
              {/* Animated Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 via-orange-500/0 to-orange-500/0 group-hover:from-orange-500/10 group-hover:via-orange-500/5 group-hover:to-transparent transition-all duration-500"></div>
              
              <div className="relative z-10">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-full group-hover:from-orange-500/40 group-hover:to-orange-600/40 transition-all duration-300">
                    <Building2 size={48} className="text-orange-400 group-hover:text-orange-300 transition-colors duration-300" />
                  </div>
                </div>
                <h4 className="text-2xl font-bold mb-3 text-white group-hover:text-orange-400 transition-colors duration-300">
                  Partita IVA e Aziende
                </h4>
                <p className="text-gray-400 text-lg">Fino a 250 dipendenti</p>
                <div className="mt-4 flex items-center justify-center text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-sm font-semibold">Scopri di più →</span>
                </div>
              </div>
            </Link>
            
            {/* Large Companies */}
            <Link 
              to="/grandi-aziende"
              className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-2xl p-8 hover:shadow-2xl hover:shadow-blue-500/30 transition-all duration-300 transform hover:scale-105 group border border-gray-700 hover:border-blue-500 overflow-hidden"
            >
              {/* Animated Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-blue-500/0 to-blue-500/0 group-hover:from-blue-500/10 group-hover:via-blue-500/5 group-hover:to-transparent transition-all duration-500"></div>
              
              <div className="relative z-10">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full group-hover:from-blue-500/40 group-hover:to-blue-600/40 transition-all duration-300">
                    <Users size={48} className="text-blue-400 group-hover:text-blue-300 transition-colors duration-300" />
                  </div>
                </div>
                <h4 className="text-2xl font-bold mb-3 text-white group-hover:text-blue-400 transition-colors duration-300">
                  Grandi Aziende
                </h4>
                <p className="text-gray-400 text-lg">Oltre i 250 dipendenti</p>
                <div className="mt-4 flex items-center justify-center text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-sm font-semibold">Scopri di più →</span>
                </div>
              </div>
            </Link>
            
            {/* Public Administration */}
            <Link 
              to="/pubblica-amministrazione"
              className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-2xl p-8 hover:shadow-2xl hover:shadow-emerald-500/30 transition-all duration-300 transform hover:scale-105 group border border-gray-700 hover:border-emerald-500 overflow-hidden"
            >
              {/* Animated Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 via-emerald-500/0 to-emerald-500/0 group-hover:from-emerald-500/10 group-hover:via-emerald-500/5 group-hover:to-transparent transition-all duration-500"></div>
              
              <div className="relative z-10">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-full group-hover:from-emerald-500/40 group-hover:to-emerald-600/40 transition-all duration-300">
                    <Landmark size={48} className="text-emerald-400 group-hover:text-emerald-300 transition-colors duration-300" />
                  </div>
                </div>
                <h4 className="text-2xl font-bold mb-3 text-white group-hover:text-emerald-400 transition-colors duration-300">
                  Pubblica Amministrazione
                </h4>
                <p className="text-gray-400 text-lg">Soluzioni e Convenzioni</p>
                <div className="mt-4 flex items-center justify-center text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-sm font-semibold">Scopri di più →</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;